import { useState } from "react";
import type { ReactElement, ReactNode } from "react";
import type { SupplierContract, SupplierContractTerm, Unternehmen } from "@kundeninfo/api-client";
import { monatPlus, tagPlus, useBestaetigung } from "@kundeninfo/ui";
import { Abschnitt, Chip, Eingabe, Hinweis, Objekt, Tabelle, statusRang } from "@kundeninfo/ui/me-design";
import type { ObjektProps } from "@kundeninfo/ui/me-design";
import { KONTIERUNG_TEXT } from "../../components/kontierungTexts.js";
import { LoeschKnopf } from "../../components/meHuellen.js";
import { Vorgangsdateien } from "../../components/Vorgangsdateien.js";
import { errorProp } from "../../components/fieldErrors.js";
import { apiMeldung } from "../../components/vorgangsdetail.js";
import { useAssignmentNames } from "../../components/KontierungFelder.js";
import type { ListClient } from "../../components/KontierungFelder.js";
import { kennzeichenFolge, kennzeichenTeil, unternehmenKuerzel } from "../../components/objektPanel/kennzeichen.js";
import type { Kennzeichen } from "../../components/objektPanel/kennzeichen.js";
import { objektFuss } from "../../components/objektPanel/ErsteSpalte.js";
import { useKennungsAttribut } from "../../components/objektPanel/kennungsAttribut.js";
import { oeffneOderSpringe, useObjektPanel } from "../../components/objektPanel/ObjektPanelKontext.js";
import { formatDatum, formatDezimalBetrag } from "../../format.js";
import { ROW_TEXT } from "../customerOverview/texts.js";
import { TicketAnlegen } from "../tickets/TicketAnlegen.js";
import type { TicketsClient } from "../tickets/ticketsClient.js";
import { ChargeList } from "./ChargeList.js";
import { KonditionPanel } from "./KonditionPanel.js";
import { VertragPanel } from "./VertragPanel.js";
import type { SupplierContractsClient } from "./KundeLiefervertraege.js";
import { ActionQuestion, contractStatus, perCycleText, runtimeText, today } from "./shared.js";
import { SUPPLIER_TEXT_WEB as T } from "./texts.js";
import "./Suppliers.css";

/**
 * DER LIEFERVERTRAG ALS `Objekt` (Spec „Umstellung auf Objekt“, P9, K12; Plan Aufgabe 3) — die EINE Zeile dieser Art in
 * Akte, Einkauf und Objektpanel. Die Teile ordnet eine reine Funktion zu (`liefervertragTeile`), damit die drei Orte nicht
 * auseinanderlaufen. Aufgeklappt stehen die Details als Kinder (`ContractDetail`, ohne eigene Aktionsleiste); im Fuß der
 * Verweis (Panelknopf = Bearbeiten; Sprung nur außerhalb der eigenen Akte, im Panel steht er links unten) und das „⋯“ mit
 * „Kondition ändern ab …“, „Kündigung erfassen“, „Ticket anlegen“. Die Aktionen selbst (Panels, Frage, Ticket) hält
 * `useContractActions` — ebenfalls EINMAL für alle drei Orte (PF19).
 */

export type LiefervertragTeile = Pick<ObjektProps, "name" | "kennung" | "bezug" | "art" | "wert" | "status" | "statusArt" | "zeit">
  & { kennzeichen: Kennzeichen[]; angaben: string[] };

const fristText = (f: NonNullable<SupplierContract["noticeDeadline"]>): string => T.noticeDeadline(formatDatum(f.lastNoticeDay));

/** Die Teile nach Anatomie 1.2: Name Titel, Kennung Referenz, Bezug Vertragsart (außerhalb der Akte mit Lieferant), Wert je
 *  Rhythmus, Status Zustand, Zeit nächste Fälligkeit, Kennzeichen Unternehmen (nur bei mehreren in der Sicht, PF20),
 *  Angaben Laufzeit und Kündigungsfrist. Liegt die Frist in der Vorwarnzeit (`soon` vom Server, Aufgabe 22) und steht der
 *  Zustand besser als „warnung“, ist sie der Status (Ruling 712 wie 705: der schlechteste nach status.js) statt leise in den
 *  Angaben. */
export function liefervertragTeile(c: SupplierContract,
  o: { mitLieferant: boolean; mehrereUnternehmen?: boolean; sitzung: readonly Unternehmen[] }): LiefervertragTeile {
  const zustand = contractStatus(c);
  const frist = c.noticeDeadline;
  const dringend = frist !== null && frist.soon && statusRang("warnung") < statusRang(zustand.art) ? frist : null;
  const status = dringend === null ? zustand : { text: fristText(dringend), art: "warnung" as const };
  return {
    name: c.title,
    ...(c.supplierReference === null ? {} : { kennung: c.supplierReference }),
    bezug: [c.contractTypeName, ...(o.mitLieferant ? [c.customerName] : [])].join(" · "),
    art: "vertrag",
    ...(c.currentTerm === null ? {} : { wert: perCycleText(c.currentTerm.netAmount, c.currentTerm.cycle) }),
    status: status.text, statusArt: status.art,
    ...(c.nextDue === null ? {}
      : { zeit: T.nextDue(formatDatum(c.nextDue.dueDate), formatDezimalBetrag(c.nextDue.grossAmount, c.currency)) }),
    kennzeichen: kennzeichenFolge({ unternehmen: unternehmenKuerzel(c.companyId, o.sitzung, o.mehrereUnternehmen) }, o.sitzung),
    angaben: [runtimeText(c), ...(frist === null || dringend !== null ? [] : [fristText(frist)])],
  };
}

export type LiefervertragAktion = "edit" | "term" | "terminate" | "ticket";
/** Wo das Objekt steht: in der Akte seines Lieferanten (auch im Objektpanel über ihr), im Einkauf (Lieferant im Bezug,
 *  Sprung) oder im Objektpanel über einem anderen Ort (Lieferant im Bezug, Sprung links unten im Panel, P9). */
export type LieferantenOrt = "akte" | "einkauf" | "panel";

export function LiefervertragObjekt(p: {
  vertrag: SupplierContract; ort: LieferantenOrt; aufgeklappt: boolean; onAufgeklappt: (offen: boolean) => void;
  client: SupplierContractsClient; lists: ListClient; companies: readonly Unternehmen[]; mehrereUnternehmen: boolean;
  /** Zählt jedes geglückte Laden der Liste — die Fälligkeiten lesen damit frisch. */
  refresh: number; mitTicket: boolean;
  onAktion: (a: LiefervertragAktion) => void; onGeaendert: () => void; gesperrt: boolean;
  /** Ohne Angabe springt „Ordner“ in die Dateien des Lieferanten — über `sprungZiel` samt Herkunft und durch den Ausgang
   *  des Gastgebers (PF3, F10). */
  onOrdnerOeffnen?: (folderId: string) => void; jumpNote?: string | null;
  /** Im Objektpanel: eine Datei des Vertrags wurde geschrieben (F9) — die Ebene gilt als geändert. */
  onDateienGeaendert?: () => void;
}): ReactElement {
  const c = p.vertrag;
  const wege = useObjektPanel();
  const merke = useKennungsAttribut("data-contract-id", c.id);
  const t = liefervertragTeile(c, { mitLieferant: p.ort !== "akte", mehrereUnternehmen: p.mehrereUnternehmen,
    sitzung: p.companies });
  const terminable = c.terminatedOn === null && c.state !== "ENDED";
  const fuss = objektFuss(wege, {
    verweis: { bezug: { art: "liefervertrag", id: c.id, customerId: c.customerId, companyId: c.companyId, label: c.title },
      eigenesPanel: () => p.onAktion("edit"), ...(p.ort === "einkauf" ? {} : { ohneSprung: true }) },
    menue: { name: T.actionsFor(c.title), onWahl: (w) => p.onAktion(w as LiefervertragAktion), eintraege: [
      { wert: "term", text: T.changeTerm },
      ...(terminable ? [{ wert: "terminate", text: T.terminate, gefaehrlich: true }] : []),
      ...(p.mitTicket ? [{ wert: "ticket", text: T.createTicket }] : []),
    ] },
  });
  const ordner = p.onOrdnerOeffnen
    ?? ((folderId: string) => wege.springe({ art: "akte", id: c.customerId, reiter: "dateien", folderId, label: c.customerName }));
  const { kennzeichen, angaben, ...teile } = t;
  return (
    <Objekt ref={merke} {...teile} klappbar aufgeklappt={p.aufgeklappt}
      onAufgeklappt={(d) => p.onAufgeklappt(d.aufgeklappt)} kennzeichen={kennzeichenTeil(kennzeichen)} fuss={fuss}
      angaben={angaben.join(" · ")}>
      {p.aufgeklappt && (
        <ContractDetail contract={c} client={p.client} lists={p.lists} refresh={p.refresh} companies={p.companies}
          onOrdnerOeffnen={ordner} jumpNote={p.jumpNote ?? null} locked={p.gesperrt} onChanged={p.onGeaendert}
          {...(p.onDateienGeaendert === undefined ? {} : { onFilesChanged: p.onDateienGeaendert })} />
      )}
    </Objekt>
  );
}

/** Der Ticketweg der Stelle (Akte, Einkauf, Gastgeber): ohne ihn kein „Ticket anlegen“. */
export type TicketWeg = { client: TicketsClient; ich: { id: string; name: string }; arbeitsunternehmenId: string | null };

type Offen = { kind: Exclude<LiefervertragAktion, "ticket"> | "ticket"; contract: SupplierContract };

/**
 * DIE AKTIONEN EINES VERTRAGS (PF19, PF20) — Akte, Einkauf und Objektpanel nutzen denselben Weg: „Bearbeiten“
 * (`VertragPanel`), „Kondition ändern ab …“ (`KonditionPanel`), „Kündigung erfassen“ (gestapelte Frage), „Ticket anlegen“
 * (`TicketAnlegen`, danach das neue Ticket im Panel bzw. per Sprung). Ohne festen Kunden: `start` nimmt Kunde und
 * Unternehmen vom Vertrag (der Einkauf zeigt viele Lieferanten). Die Panels tragen `verlauf` (696, PF9 Weg A). Nach jedem
 * Schreibweg — auch einem gescheiterten — `onDone` mit der Kennung (die Stelle lädt im Stehen nach).
 */
export function useContractActions(a: { client: SupplierContractsClient; lists: ListClient; companies: readonly Unternehmen[];
  ticket?: TicketWeg; onDone: (contractId: string) => void }): {
  start(kind: LiefervertragAktion, c: SupplierContract): void; offen: boolean; mitTicket: boolean; panels: ReactNode;
} {
  const [offen, setOffen] = useState<Offen | null>(null);
  const wege = useObjektPanel();
  const done = (id: string) => (): void => { setOffen(null); a.onDone(id); };
  const close = (id: string) => (reload: boolean): void => { if (reload) done(id)(); else setOffen(null); };
  const o = offen;
  const kuerzel = (c: SupplierContract): string[] => a.companies.filter((u) => u.id === c.companyId).map((u) => u.shortCode);
  const panels = o === null ? null
    : o.kind === "edit" ? (
      <VertragPanel customerId={o.contract.customerId} companyId={o.contract.companyId} client={a.client} lists={a.lists}
        vorhandener={o.contract} verlauf onGespeichert={done(o.contract.id)} onSchliessen={close(o.contract.id)} />
    ) : o.kind === "term" ? (
      <KonditionPanel customerId={o.contract.customerId} contract={o.contract} client={a.client} verlauf
        onGespeichert={done(o.contract.id)} onSchliessen={close(o.contract.id)} />
    ) : o.kind === "terminate" ? (
      <TerminateQuestion contract={o.contract} client={a.client} onDone={done(o.contract.id)} onClose={() => setOffen(null)}
        onStale={done(o.contract.id)} />
    ) : a.ticket === undefined ? null : (
      <TicketAnlegen client={a.ticket.client} companies={a.companies} ich={a.ticket.ich} vorgabeCompanyId={o.contract.companyId}
        arbeitsunternehmenId={a.ticket.arbeitsunternehmenId}
        kunde={{ id: o.contract.customerId, name: o.contract.customerName, kuerzel: kuerzel(o.contract) }}
        bezug={{ kind: "SUPPLIER_CONTRACT", id: o.contract.id, label: T.ticketReference(o.contract.title),
          companyId: o.contract.companyId }}
        onAbbrechen={() => setOffen(null)}
        onAngelegt={(id) => { setOffen(null); oeffneOderSpringe(wege, { art: "ticket", id }); }} />
    );
  return {
    start: (kind, c) => { if (offen === null && (kind !== "ticket" || a.ticket !== undefined)) setOffen({ kind, contract: c }); },
    offen: offen !== null, mitTicket: a.ticket !== undefined, panels,
  };
}

type Sections = { terms: boolean; charges: boolean; files: boolean };

/** „2 Jahre“ — ein Wert/Einheit-Paar der Laufzeit (P26). */
const pairText = (value: number | null, unit: keyof typeof T.termUnit | null): string | null =>
  (value === null || unit === null ? null : `${String(value)} ${T.termUnit[unit]}`);

/** „am 15.“, „am 15. Januar“, „zu Beginn des Zeitraums“, mit Versatz „+3 Tage“. */
function dueText(t: SupplierContractTerm): string {
  const rule = t.dueRule === "FIXED_DAY" && t.dueDayOfMonth !== null
    ? T.dueOnDay(t.dueDayOfMonth, t.dueMonth === null ? null : T.month(t.dueMonth)) : T.dueRules[t.dueRule];
  return t.dueOffsetDays === 0 ? rule : `${rule} ${T.offsetText(t.dueOffsetDays)}`;
}

/** Das Aufgeklappte eines Vertrags (Details als Kinder des `Objekt`, P9): Kopfdaten, Konditionen, Fälligkeiten, Dateien —
 *  ohne eigene Aktionsleiste, die Aktionen stehen im Fuß. */
function ContractDetail(p: {
  contract: SupplierContract; client: SupplierContractsClient; lists: ListClient; refresh: number;
  companies: readonly Unternehmen[]; onOrdnerOeffnen: (folderId: string) => void; locked: boolean; onChanged: () => void;
  jumpNote: string | null; onFilesChanged?: () => void;
}): ReactElement {
  const customerId = p.contract.customerId;
  const c = p.contract;
  const { names, failed: namesFailed } = useAssignmentNames(p.lists, c.companyId);
  const [sections, setSections] = useState<Sections>({ terms: true, charges: true, files: false });
  const [fileCount, setFileCount] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { bestaetigen } = useBestaetigung();

  async function deleteTerm(t: SupplierContractTerm): Promise<void> {
    const ab = formatDatum(t.validFrom);
    if (!(await bestaetigen({ titel: T.confirmDeleteTerm(ab), text: T.deleteConsequence, bestaetigenText: T.deleteAction,
      gefaehrlich: true }))) return;
    setNotice(null);
    try {
      await p.client.deleteContractTerm(customerId, c.id, t.id, c.version);
    } catch (e) {
      setNotice(apiMeldung(e, T.actionFailed));
    }
    p.onChanged();
  }

  /** Ein Name aus den Listen; solange sie laden „…“, nach einem Ladefehler „–“ (mit Hinweis oben, Review 13 W3). */
  const name = (map: ReadonlyMap<string, string> | undefined, id: string | null): string =>
    (id === null || namesFailed ? T.none : map?.get(id) ?? T.pending);
  const noticeLine = c.noticeDeadline === null ? null : T.noticeDeadline(formatDatum(c.noticeDeadline.lastNoticeDay));
  const noticePair = pairText(c.noticePeriodValue, c.noticePeriodUnit);
  const facts: [string, string | null][] = [
    [T.billingModeLabel, T.billingMode[c.billingMode]],
    [T.paymentMethodLabel, T.paymentMethod[c.paymentMethod]],
    [T.reference, c.supplierReference],
    [T.columnRuntime, runtimeText(c)],
    [T.minimumTerm, pairText(c.minimumTermValue, c.minimumTermUnit)],
    [T.renewal, pairText(c.renewalPeriodValue, c.renewalPeriodUnit)],
    [T.noticePeriod, noticePair === null ? null : [noticePair, c.noticeReference === null ? null
      : T.noticeReference[c.noticeReference], noticeLine].filter((x) => x !== null).join(", ")],
    [T.terminatedOn, c.terminatedOn === null ? null : formatDatum(c.terminatedOn)],
    [T.settlementMonth, c.settlementMonth === null ? null : T.month(c.settlementMonth)],
    [T.planFromFact, c.planFrom === null ? T.planFromDefaultLine(formatDatum(c.effectivePlanFrom))
      : T.planFromMonth(Number(c.planFrom.slice(5, 7)), c.planFrom.slice(0, 4))],
    [KONTIERUNG_TEXT.account, name(names?.accounts, c.bookingAccountId)],
    [KONTIERUNG_TEXT.costCentre, name(names?.costCentres, c.costCentreId)],
    [T.note, c.note],
  ];
  const latest = c.terms.at(-1) ?? null;
  const deletable = latest !== null && c.terms.length > 1 && !latest.inUse ? latest : null;
  const toggle = (k: keyof Sections) => (d: { offen: boolean }): void => setSections((s) => ({ ...s, [k]: d.offen }));

  return (
    <div className="sup__detail">
      {p.jumpNote !== null && <p className="sup__jump-note" role="status">{p.jumpNote}</p>}
      {notice !== null && <Hinweis art="fehler">{notice}</Hinweis>}
      {namesFailed && <Hinweis art="warnung">{T.namesLoadFailed}</Hinweis>}
      <h4 className="sup__detail-title">{T.head}</h4>
      <dl className="sup__facts">
        {facts.filter(([, v]) => v !== null).map(([k, v]) => (
          <div key={k} className="sup__fact"><dt>{k}</dt><dd>{v}</dd></div>
        ))}
      </dl>

      <Abschnitt titel={T.terms} zaehler={c.terms.length} offen={sections.terms} onUmgeschaltet={toggle("terms")}>
        <Tabelle dicht stapeln>
          <table>
            <thead>
              <tr>
                <th scope="col">{T.termFrom}</th>
                <th scope="col" data-ausrichtung="rechts">{T.netAmount}</th>
                <th scope="col">{T.dueRuleLabel}</th>
                <th scope="col">{T.taxCaseLabel}</th>
                <th scope="col">{T.columnState}</th>
              </tr>
            </thead>
            <tbody>
              {c.terms.map((t) => (
                <tr key={t.id} data-term-id={t.id}>
                  <td>{T.from(formatDatum(t.validFrom))}</td>
                  <td data-ausrichtung="rechts">{perCycleText(t.netAmount, t.cycle)}</td>
                  <td>{dueText(t)}</td>
                  <td>{T.taxCase[t.taxCase]}</td>
                  <td>
                    {c.currentTerm?.id === t.id && <Chip statusArt="ok">{T.current}</Chip>}
                    {deletable?.id === t.id && (
                      <LoeschKnopf name={T.deleteTerm(formatDatum(t.validFrom))} deaktiviert={p.locked}
                        onClick={() => { void deleteTerm(t); }} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tabelle>
      </Abschnitt>

      <Abschnitt titel={T.charges} offen={sections.charges} onUmgeschaltet={toggle("charges")}>
        {sections.charges && (
          <ChargeList customerId={customerId} contract={c} client={p.client} refresh={p.refresh} locked={p.locked}
            onChanged={p.onChanged} />
        )}
      </Abschnitt>

      <Abschnitt titel={T.files} offen={sections.files} onUmgeschaltet={toggle("files")}
        {...(fileCount === null ? {} : { zaehler: fileCount })}>
        {sections.files && (
          <Vorgangsdateien client={p.client} kundeId={customerId} bezug={{ art: "supplier-contracts", id: c.id }}
            vorgangName={ROW_TEXT.firstLink("SUPPLIER_CONTRACT", c.title)} firmen={p.companies} lesend={null}
            onZahl={setFileCount} onOrdnerOeffnen={p.onOrdnerOeffnen}
            {...(p.onFilesChanged === undefined ? {} : { onGeaendert: p.onFilesChanged })} />
        )}
      </Abschnitt>

    </div>
  );
}

/** Das vorbelegte Vertragsende einer Kündigung: der Stichtag des Servers (`noticeDeadline.endDate`), eine Befristung,
 *  sonst heute + Frist — jederzeit kündbar (Entscheid 544, 0 Tage = heute) oder ohne berechenbaren Stichtag; ohne Frist
 *  heute (Fixrunde 1 zu Aufgabe 37). Monate am Monatsende geklemmt wie der Server. */
function terminationEnd(c: SupplierContract, heute: string): string {
  const fixed = c.noticeDeadline?.endDate ?? c.validTo;
  if (fixed !== null) return fixed;
  const { noticePeriodValue: n, noticePeriodUnit: unit } = c;
  if (n === null || unit === null) return heute;
  return unit === "DAYS" ? tagPlus(heute, n) : monatPlus(heute, unit === "YEARS" ? n * 12 : n);
}

/** „Kündigung erfassen“ — eine direkte Aktion, also die kleine gestapelte Frage, in Gefahr-Gestalt (Rot-Regel:
 *  `useBestaetigung`; die Posten nach dem Ende entfallen): Datum der Kündigung (heute) und Vertragsende (`terminationEnd`). Befunde am
 *  Vertragsende meldet der Server selbst an `validTo` (Final-Fixrunde); einer an einem anderen Feld ist eine fremde
 *  Fachregel der Frage. */
function TerminateQuestion(p: {
  contract: SupplierContract; client: Pick<SupplierContractsClient, "terminateSupplierContract">;
  onDone: () => void; onClose: () => void; onStale: () => void;
}): ReactElement {
  const c = p.contract;
  const [terminatedOn, setTerminatedOn] = useState(today);
  const [validTo, setValidTo] = useState(() => terminationEnd(c, today()));
  return (
    <ActionQuestion title={T.confirmTerminate(validTo === "" ? T.pending : formatDatum(validTo))} confirmText={T.terminate}
      danger ready={terminatedOn !== "" && validTo !== ""} fields={["terminatedOn", "validTo"]}
      onDone={p.onDone} onClose={p.onClose} onStale={p.onStale}
      run={() => p.client.terminateSupplierContract(c.customerId, c.id, { version: c.version, terminatedOn, validTo })}>
      {(errors) => (
        <>
          <p className="sup__muted">{T.terminateConsequence}</p>
          <Eingabe art="datum" beschriftung={T.terminatedOn} wert={terminatedOn} pflicht {...errorProp(errors.terminatedOn)}
            onGeaendert={(e) => setTerminatedOn(e.wert)} />
          <Eingabe art="datum" beschriftung={T.validTo} wert={validTo} pflicht {...errorProp(errors.validTo)}
            onGeaendert={(e) => setValidTo(e.wert)} />
        </>
      )}
    </ActionQuestion>
  );
}
